import { useId, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button, Select, Textarea } from '@artosera/ui';
import type { ConseilConservationReponse } from '@artosera/shared';
import {
  espaceCollectionneurApi,
  useOeuvreEspace,
  useActionEspace,
} from '../../api/espaceCollectionneur.js';
import { oeuvresApi } from '../../api/oeuvres.js';
import { appelDeRequete, cleMetier } from '../../api/cles.js';
import { estApiError } from '../../api/errors.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import styles from './EspaceCollectionneur.module.css';

export function ConseilsConservation({
  oeuvreId,
  artisteId,
}: {
  oeuvreId: string;
  artisteId: string;
}) {
  const { galerie, galerieSlug } = useShellContext();
  const { t } = useTranslation(['regie', 'commun']);
  const fiche = useOeuvreEspace(galerieSlug, oeuvreId);
  if (fiche.isLoading) return <p role="status">{t('commun:chargement')}</p>;
  if (!fiche.data) return <p role="alert">{t('commun:erreurReseau')}</p>;
  return (
    <section className={styles.bloc}>
      <h2>{t('espaceCollectionneur.conservation')}</h2>
      <Editeur
        key={`${oeuvreId}-${fiche.data.conseil.version}`}
        oeuvreId={oeuvreId}
        artisteId={artisteId}
        initial={fiche.data.conseil}
        peutEcrire={galerieALaction(galerie, 'collector-space:write')}
        recharger={() => {
          void fiche.refetch();
        }}
      />
      <h3>{t('espaceCollectionneur.contactsPartages')}</h3>
      <ul className={styles.liste}>
        {fiche.data.contacts.map((c) => (
          <li key={c.id}>
            <Link to={`/${galerieSlug}/contacts/${c.id}`}>{c.nom}</Link> ·{' '}
            {t(`espaceCollectionneur.etats.${c.etat ?? 'aucun'}`)}
          </li>
        ))}
      </ul>
    </section>
  );
}
function Editeur({
  oeuvreId,
  artisteId,
  initial,
  peutEcrire,
  recharger,
}: {
  oeuvreId: string;
  artisteId: string;
  initial: ConseilConservationReponse;
  peutEcrire: boolean;
  recharger: () => void;
}) {
  const { galerieSlug } = useShellContext();
  const { t } = useTranslation(['regie', 'commun']);
  const id = useId();
  const [texte, setTexte] = useState(initial.texte);
  const [reprise, setReprise] = useState(false);
  const mutation = useActionEspace(galerieSlug, (corps: ConseilConservationReponse, appel) =>
    espaceCollectionneurApi.conserver(galerieSlug, oeuvreId, corps, appel),
  );
  return (
    <form
      className={styles.formulaire}
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate({ texte, version: initial.version });
      }}
    >
      {(['fr', 'en'] as const).map((langue) => (
        <Field
          key={langue}
          htmlFor={`${id}-${langue}`}
          label={t(`espaceCollectionneur.texte.${langue}`)}
        >
          <Textarea
            id={`${id}-${langue}`}
            value={texte[langue] ?? ''}
            maxLength={8000}
            readOnly={!peutEcrire}
            onChange={(e) => setTexte({ ...texte, [langue]: e.target.value })}
          />
        </Field>
      ))}
      {peutEcrire && (
        <>
          <div className={styles.gestes}>
            <Button type="submit" loading={mutation.isPending}>
              {t('commun:actions.enregistrer')}
            </Button>
            <Button variant="secondary" onClick={() => setReprise(!reprise)}>
              {t('espaceCollectionneur.reprendre')}
            </Button>
          </div>
          {reprise && (
            <Reprise
              oeuvreId={oeuvreId}
              artisteId={artisteId}
              onCopier={(copie) => {
                setTexte({ ...copie });
                setReprise(false);
              }}
            />
          )}
        </>
      )}
      {mutation.isError && (
        <>
          <p role="alert">
            {estApiError(mutation.error) && mutation.error.status === 409
              ? t('espaceCollectionneur.conflit')
              : t('commun:erreurReseau')}
          </p>
          <Button
            variant="secondary"
            onClick={() => {
              mutation.reset();
              recharger();
            }}
          >
            {t('espaceCollectionneur.recharger')}
          </Button>
        </>
      )}
      {mutation.isSuccess && <p role="status">{t('espaceCollectionneur.enregistre')}</p>}
    </form>
  );
}
function Reprise({
  oeuvreId,
  artisteId,
  onCopier,
}: {
  oeuvreId: string;
  artisteId: string;
  onCopier: (texte: ConseilConservationReponse['texte']) => void;
}) {
  const { galerieSlug } = useShellContext();
  const { t } = useTranslation(['regie', 'commun']);
  const [source, setSource] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const liste = useQuery({
    queryKey: cleMetier(galerieSlug, 'oeuvres', 'conseils-source', artisteId, page),
    queryFn: (c) => oeuvresApi.lister(galerieSlug, { artisteId, page }, appelDeRequete(c)),
  });
  const conseil = useOeuvreEspace(galerieSlug, source);
  return (
    <div className={styles.formulaire}>
      <Select
        aria-label={t('espaceCollectionneur.oeuvreSource')}
        value={source ?? ''}
        onValueChange={setSource}
        placeholder={t('espaceCollectionneur.oeuvreSource')}
        options={(liste.data?.oeuvres ?? [])
          .filter((o) => o.id !== oeuvreId)
          .map((o) => ({ value: o.id, label: o.titre }))}
      />
      {liste.data && liste.data.pagination.pages > 1 && (
        <div className={styles.gestes}>
          <Button variant="secondary" disabled={page === 1} onClick={() => setPage(page - 1)}>
            {t('espaceCollectionneur.precedent')}
          </Button>
          <Button
            variant="secondary"
            disabled={page === liste.data.pagination.pages}
            onClick={() => setPage(page + 1)}
          >
            {t('espaceCollectionneur.suivant')}
          </Button>
        </div>
      )}
      {conseil.data && (
        <>
          <p className={styles.texte}>{conseil.data.conseil.texte.fr}</p>
          <p className={styles.texte}>{conseil.data.conseil.texte.en}</p>
          <Button variant="secondary" onClick={() => onCopier(conseil.data.conseil.texte)}>
            {t('espaceCollectionneur.copier')}
          </Button>
        </>
      )}
      {(liste.isLoading || conseil.isFetching) && <p role="status">{t('commun:chargement')}</p>}
      {(liste.isError || conseil.isError) && <p role="alert">{t('commun:erreurReseau')}</p>}
    </div>
  );
}
